import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { propertyService } from '../../service/property.service';
import { propertyTypeService } from '../../service/propertyType.service';
import { citiesService } from '../../service/cities.service';
import { cachedMasterDataService } from '../../service/cachedMasterData.service';
import { Property } from '../../models/property.model';
import { PropertyType } from '../../models/propertyType.model';
import { ListItem } from '../../models/ListItem';
import { Button, Form, Modal } from 'react-bootstrap';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import * as Icon from 'react-bootstrap-icons';
import RoomTable from '../Room/roomTable';
import PageHeader from "../PageHeader";

const PropertyComponent: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);
  const [cities, setCities] = useState<ListItem[]>([]);
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const navigate = useNavigate();
  
  // Update URL when page changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  }, [page, setSearchParams]);

  useEffect(() => {
    if (!masterDataLoaded) {
      fetchMasterData();
    }
  }, [masterDataLoaded]);

  useEffect(() => {
    fetchPaginatedProperties();
  }, [page, rows, sortField, sortOrder, globalFilter]);

  const fetchMasterData = async () => {
    if (masterDataLoaded) return; // Prevent duplicate API calls
    
    const [types, cities] = await Promise.all([
      cachedMasterDataService.getPropertyTypesList(),
      cachedMasterDataService.getCitiesList()
    ]);
    setPropertyTypes(types);
    setCities(cities);
    setMasterDataLoaded(true);
  };

  const fetchPaginatedProperties = async () => {
    setListLoading(true);
    try {
      const result = await propertyService.getAllPaginated<Property>({
        pageNumber: page,
        pageSize: rows,
        sortField,
        sortOrder: sortOrder === -1 ? "desc" : "asc",
        globalFilter
      });
      setProperties((result?.items ?? []).map((item: any) => ({
        ...item,
        id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id
      })));
      setTotalRecords(result?.totalCount ?? 0);
    } catch {
      setProperties([]);
      setTotalRecords(0);
    } finally {
      setListLoading(false);
    }
  };

  const handleAdd = useCallback(() => {
    navigate('/manage-property/add', { state: { mode: 'add' } });
  }, [navigate]);

  const handleEdit = useCallback((rowData: Property) => {
    navigate(`/manage-property/edit/${rowData.id}`, { state: { mode: 'edit', propertyId: rowData.id } });
  }, [navigate]);

  const handleDelete = async (rowData: Property) => {
    if (!rowData.id) return;
    await propertyService.delete(rowData.id);
    fetchPaginatedProperties();
  };

  const handleToggleActive = async (rowData: Property) => {
    // const updated = { ...property, isActive: !property.isActive };
    await propertyService.updateStatus(rowData.id!);
    fetchPaginatedProperties();
  };

  const renderTruncated = (value?: string) => {
    if (!value) return '';
    return value.length > 30 ? (
      <span title={value} style={{ cursor: 'pointer' }}>{value.slice(0, 30)}...</span>
    ) : value;
  };

  const renderLink = (value?: string, label?: string) =>
    value ? <a href={value} target="_blank" rel="noopener noreferrer">{label}</a> : null;

  const actionBodyTemplate = (rowData: Property) => (
    <div className="d-flex align-items-center gap-2">
      <Button variant="link" className="p-0" onClick={() => handleEdit(rowData)} title="Edit" style={{ color: '#198754' }}>
        <i className="bi bi-pencil"></i>
      </Button>
      <Button variant="link" className="p-0" onClick={() => handleDelete(rowData)} title="Delete" style={{ color: '#dc3545' }}>
        <i className="bi bi-trash"></i>
      </Button>
      <Form.Check
        type="switch"
        id={`isActiveSwitch${rowData.id}`}
        checked={rowData.isActive}
        onChange={() => handleToggleActive(rowData)}
        className="m-0"
        title="Toggle Active"
      />
    </div>
  );

  const mainImageBodyTemplate = (rowData: Property) => {
    let url: string | undefined = undefined;
    if (rowData.propertyImages && rowData.propertyImages.length) {
      const main = rowData.propertyImages.find(pi => pi.isMainImage) || rowData.propertyImages[0];
      url = main?.imageUrl;
    } else if (rowData.imageUrl) {
      url = rowData.imageUrl;
    }
    if (!url) return <span className="text-muted" style={{ fontSize: '0.75rem' }}>No Image</span>;
    const handleClick = () => {
      const imgs = rowData.propertyImages && rowData.propertyImages.length
        ? rowData.propertyImages.map(pi => pi.imageUrl)
        : [url!];
      setPreviewImages(imgs.filter(Boolean));
      const mainIdx = imgs.findIndex(i => i === url);
      setPreviewIndex(mainIdx >= 0 ? mainIdx : 0);
      setPreviewVisible(true);
    };
    return (
      <button type="button" onClick={handleClick} className="p-0 border-0 bg-transparent" style={{ width: 50, height: 40, position: 'relative', cursor: 'zoom-in' }}>
        <img
          src={url}
          alt={rowData.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4, border: '1px solid #e3e3e3' }}
          onError={(e: any) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
      </button>
    );
  };

  return (
    <div className="content">
      {/* <div className="content-header">
        <div className='container d-flex align-items-center justify-content-between'>
          <div className='d-flex flex-column'>
            <h6 className="_600 mb-2 page-heading">Properties List</h6>
          </div>
        </div>
      </div> */}
      {!selectedProperty && (
         <PageHeader
          title="Property List"
          showBackButton={false}
        />
      )}
      <div className="container mt-4">
        {!selectedProperty ? (
          <DataTable
            value={properties}
            paginator
            rows={rows}
            first={(page - 1) * rows}
            totalRecords={totalRecords}
            onPage={e => {
              setPage((e.page ?? 0) + 1);
              setRows(e.rows);
            }}
            lazy
            loading={listLoading}
            rowsPerPageOptions={[5, 10, 20]}
            sortMode="single"
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={e => {
              setSortField(e.sortField || '');
              setSortOrder(e.sortOrder as 1 | -1);
              setPage(1);
            }}
            emptyMessage="No properties found."
            dataKey="id"
            className="grid-table"
            header={
              <div className="d-flex justify-content-between align-items-center gap-3">
                <div className="d-flex align-items-center gap-2">
                  <IconField iconPosition="left">
                    <InputIcon><Icon.Search className="tx-12" /></InputIcon>
                    <InputText type="text" value={globalFilter} onChange={e => setGlobalFilter(e.target.value)} placeholder="Search" style={{ minWidth: 300 }} />
                  </IconField>
                  {globalFilter && (
                    <button type="button" className="btn btn-link p-0 ms-2" onClick={() => setGlobalFilter('')} aria-label="Clear search" >
                      &times;
                    </button>
                  )}
                </div>
                <Button variant="primary" style={{ minWidth: 150 }} onClick={handleAdd}>
                  <i className="bi bi-plus-circle me-2" /> Create Property
                </Button>
              </div>
            }
            selectionMode="single"
          >
            <Column header="Image" body={mainImageBodyTemplate} style={{ width: '70px' }} />
            <Column field="name" header="Name" sortable />
            <Column field="description" header="Description" body={rowData => renderTruncated(rowData.description)} sortable />
            <Column field="propertyTypeId" header="Type" body={rowData => propertyTypes.find(t => t.id === rowData.propertyTypeId)?.name || ''} sortable />
            <Column field="address" header="Address" body={rowData => renderTruncated(rowData.address)} sortable />
            {/* <Column field="location" header="Location" sortable /> */}
            {/* <Column field="landmark" header="Landmark" sortable /> */}
            {/* <Column field="totalInventory" header="Total Inventory" sortable /> */}
            <Column field="cityName" header="City" body={rowData => rowData.cityName || ''} sortable />
            <Column header="Rooms" body={(rowData: Property) => (
                <Button
                  variant="link"
                  className="p-0"
                  title="View Rooms"
                  onClick={() => navigate(`/admin/property/${rowData.id}/rooms`, { state: { propertyName: rowData.name } })}
                >
                  <i className="bi bi-door-open" style={{ fontSize: '1.2rem', color: '#0d6efd' }}></i>
                </Button>
              )} style={{ textAlign: 'center', width: '80px' }} />
            <Column header="Actions" body={actionBodyTemplate} style={{ minWidth: '120px' }} />
          </DataTable>
        ) : (
          <RoomTable
            proposalId={selectedProperty.id || 0}
            propertyId={selectedProperty.id!}
            propertyName={selectedProperty.name!}
            onBack={() => setSelectedProperty(null)}
            hideSelectionCheckbox={true}
          />
        )}
      </div>
      <Modal show={previewVisible} onHide={() => setPreviewVisible(false)} centered size="lg" backdropClassName="property-image-preview-backdrop">
        <Modal.Header closeButton>
          <Modal.Title>Property Images</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewImages.length > 0 && (
            <div style={{ position: 'relative' }}>
              <img
                src={previewImages[previewIndex]}
                alt={`Property image ${previewIndex + 1}`}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 6 }}
              />
              {previewImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className="btn btn-light position-absolute"
                    style={{ top: '50%', left: 0, transform: 'translateY(-50%)' }}
                    onClick={() => setPreviewIndex((previewIndex - 1 + previewImages.length) % previewImages.length)}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="btn btn-light position-absolute"
                    style={{ top: '50%', right: 0, transform: 'translateY(-50%)' }}
                    onClick={() => setPreviewIndex((previewIndex + 1) % previewImages.length)}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                  <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                    {previewImages.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setPreviewIndex(idx)}
                        className={`p-0 border ${idx === previewIndex ? 'border-primary' : 'border-secondary'} bg-transparent`}
                        style={{ width: 48, height: 36, overflow: 'hidden', borderWidth: 2, borderRadius: 4 }}
                        aria-label={`Go to image ${idx + 1}`}
                      >
                        <img src={img} alt={`thumb-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {previewImages.length === 0 && <div className="text-muted">No images to display</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreviewVisible(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default PropertyComponent;
